import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { McpToolContractSchema } from "../../../../schemas/McpServerTool/contract/__json.schema";
import { McpToolSchemaHashSchema } from "../../../../schemas/McpServerTool/schemaHash.schema";

/**
 * One discovered tool, as an administrator sees it while deciding.
 *
 * `discoveredContract` is the whole MCP `Tool` object minus `_meta` and `icons`,
 * so name, description and input schema — the three things the acceptance
 * criterion names — are all inside it, in the exact form the hash was taken
 * over. Projecting them into three flat fields would give the approval surface a
 * second, unhashed rendering of the thing being approved, and an admin would then
 * be approving a summary rather than the contract.
 *
 * `approvedContract` is beside it rather than instead of it: on a CHANGED tool
 * the two differ, and showing an admin what they approved next to what the server
 * now advertises is the whole of that decision.
 */
export const McpServerToolViewSchema = z.object({
  id: z.string().uuid(),
  serverId: z.string().uuid(),
  name: z.string(),
  status: z.enum(DbEnum.MCP_TOOL_STATUS_VALUES),
  /**
   * `true` exactly when an agent may be attached to this tool right now.
   *
   * Derived from `status` by `isMcpToolAttachable`, and carried rather than left
   * to the reader because "not attachable" has four causes with four different
   * remedies (approve it, re-approve the changed contract, the server withdrew
   * it, an admin declined it) and a client that re-derives the predicate is a
   * fourth place the rule can be got wrong.
   */
  attachable: z.boolean(),
  discoveredContract: McpToolContractSchema,
  discoveredSchemaHash: McpToolSchemaHashSchema,
  lastDiscoveredAt: z.coerce.date(),
  removedAt: z.coerce.date().nullable(),
  approvedContract: McpToolContractSchema.nullable(),
  approvedSchemaHash: McpToolSchemaHashSchema.nullable(),
  approvedAt: z.coerce.date().nullable(),
  approvedByUserId: z.string().nullable(),
  rejectedAt: z.coerce.date().nullable(),
  rejectedByUserId: z.string().nullable()
});

export type McpServerToolView = z.infer<typeof McpServerToolViewSchema>;
